import PropTypes from 'prop-types';
import styled from 'styled-components';
import { border } from 'styled-system';

import { Box } from '@hivebrite/design-system/Box';
import { Divider } from '@hivebrite/design-system/Divider';
import { primaryShadow } from '@hivebrite/design-system/Theme';

import { moneyFromCents } from 'hb-react/shared/apps/Memberships/utils/money';
import ModalWithTemplate from 'hb-react/shared/components/DesignSystem/Modal/ModalWithTemplate';
import { Price } from 'hb-react/shared/components/Price';
import useFrameTheme from 'hb-react/shared/hooks/theme/useFrameTheme';
import useFeatureFlag from 'hb-react/shared/hooks/useFeatureFlag';
import ThemeProvider from 'hb-react/style/ThemeProvider';

import ShoppingCardBody from './components/CardBody';
import { useShoppingCardContext } from './ShoppingCardContext';

export const EmptyCard = () => (
  <Box>{s__('ShoppingCard|Your cart is empty')}</Box>
);

const Header = s__('ShoppingCard|Order summary');

const Total = ({ items = [], currency }) => {
  const { flagEvCurrenciesWithoutCents } = useFeatureFlag();

  const displayCurrency =
    currency ??
    items.find(
      ({ price = 0, total_in_cents: totalInCents }) =>
        Number(totalInCents) > 0 || price > 0,
    )?.currency;

  const total = items.reduce(
    (total, { quantity = 1, price = 0, total_in_cents: totalInCents = 0 }) =>
      total +
      quantity *
        (price ||
          moneyFromCents(
            totalInCents,
            displayCurrency?.code,
            flagEvCurrenciesWithoutCents,
          ).total),
    0,
  );

  return (
    <Box font="body-semibold-asphalt">
      <Price price={total} currency={displayCurrency} />
    </Box>
  );
};

const ShoppingCardStatic = ({ header, items = [] }) => (
  <>
    {header && (
      <Box font="h3-semibold" mb={5}>
        {header}
      </Box>
    )}
    <Box>
      {items.length === 0 ? <EmptyCard /> : <ShoppingCardBody items={items} />}
    </Box>
    <Divider borderBottomColor="horizon" mt={4} width="100%" />
    <Box justifyContent="space-between">
      <Box font="body-semibold-asphalt">{s__('ShoppingCard|Total')}</Box>
      <Total items={items} />
    </Box>
  </>
);

ShoppingCardStatic.propTypes = {
  header: PropTypes.oneOfType([PropTypes.node, PropTypes.string]),
};

const ShoppingCardModal = (props) => {
  const { closeModal, isModalOpen } = useShoppingCardContext();

  if (!isModalOpen) {
    return null;
  }

  return (
    <ModalWithTemplate
      onCloseModal={closeModal}
      width="474px"
      header={Header}
      screenReadersCloseButtonText={s__(
        'ShoppingCardsAccessibility|Close shopping cart summary window',
      )}
    >
      <Box flexDirection="column" flexGrow={1}>
        <ShoppingCardStatic {...props} />
      </Box>
    </ModalWithTemplate>
  );
};

const ShoppingCard = ({
  displayInModal = false,
  items = [],
  wrapperProps = {},
}) => {
  const frameTheme = useFrameTheme();

  if (displayInModal) {
    return <ShoppingCardModal items={items} />;
  }

  return (
    <ThemeProvider
      theme={frameTheme.themeName}
      fontBrightness={frameTheme.fontBrightness}
    >
      <Wrapper bg={frameTheme.backgroundColor} {...wrapperProps}>
        <ShoppingCardStatic header={Header} items={items} />
      </Wrapper>
    </ThemeProvider>
  );
};

ShoppingCard.propTypes = {
  displayInModal: PropTypes.bool,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      quantity: PropTypes.number,
      price: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      currency: PropTypes.shape({
        code: PropTypes.string.isRequired,
      }).isRequired,
      render: PropTypes.func,
      onEdit: PropTypes.func,
    }),
  ),
};

export default ShoppingCard;

const Wrapper = styled(Box).attrs(({ theme, borderRadius }) => ({
  flexDirection: 'column',
  px: 4,
  py: 5,
  flexGrow: 1,
  borderRadius: borderRadius ?? {
    _: 0,
    l: theme.themeVariables.globalBorderRadius,
  },
}))`
  ${border}
  width: 100%;
  ${({ $displayPrimaryShadow }) => $displayPrimaryShadow && primaryShadow};
`;
